import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Users, Box, TrendingUp, XCircle, DollarSign, Zap, Activity } from 'lucide-react';
import { StatCard } from '../components/StatCard';
import { LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const Dashboard = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/admin/stats`);
      setStats(response.data);
    } catch (error) {
      console.error('Failed to fetch stats:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-violet-500" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="dashboard-page">
      {/* Header */}
      <div>
        <h1 className="text-4xl font-heading font-bold text-white mb-2">Dashboard</h1>
        <p className="text-muted-foreground">Overview of your platform's performance and metrics</p>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <StatCard 
          title="Total Users" 
          value={stats?.totalUsers || 0} 
          change="+12.5%" 
          trend="up"
          icon={Users} 
        />
        <StatCard 
          title="Active Builds" 
          value={stats?.activeBuilds || 0} 
          icon={Box} 
        />
        <StatCard 
          title="Builds Today" 
          value={stats?.buildsToday || 0} 
          change="+8%" 
          trend="up"
          icon={TrendingUp} 
        />
        <StatCard 
          title="Failed Builds" 
          value={stats?.failedBuilds || 0} 
          icon={XCircle} 
        />
        <StatCard 
          title="Revenue" 
          value={`$${stats?.revenue?.toLocaleString() || 0}`} 
          change="+23%" 
          trend="up"
          icon={DollarSign} 
        />
        <StatCard 
          title="AI Token Usage" 
          value={stats?.aiTokenUsage?.toLocaleString() || 0} 
          icon={Zap} 
        />
        <StatCard 
          title="System Health" 
          value={stats?.systemHealth === 'healthy' ? 'Healthy' : 'Warning'} 
          icon={Activity} 
        />
        <StatCard 
          title="Avg Build Time" 
          value="3.2m" 
          change="-5%" 
          trend="up"
          icon={Box} 
        />
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Builds Over Time */}
        <div className="rounded-xl border border-white/10 bg-[#0A0A0A] p-6">
          <h3 className="text-lg font-heading font-semibold text-white mb-6">Builds Over Time</h3>
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={stats?.chartData || []}>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
              <XAxis 
                dataKey="date" 
                stroke="rgba(255,255,255,0.3)" 
                style={{ fontSize: '12px' }}
                tickFormatter={(value) => value.split('-')[2]}
              />
              <YAxis stroke="rgba(255,255,255,0.3)" style={{ fontSize: '12px' }} />
              <Tooltip 
                contentStyle={{ 
                  backgroundColor: '#0A0A0A', 
                  border: '1px solid rgba(255,255,255,0.1)',
                  borderRadius: '8px',
                  color: '#fff'
                }}
              />
              <Line 
                type="monotone" 
                dataKey="builds" 
                stroke="#8B5CF6" 
                strokeWidth={2}
                dot={{ fill: '#8B5CF6', r: 4 }}
              />
            </LineChart>
          </ResponsiveContainer>
        </div>

        {/* Revenue Over Time */}
        <div className="rounded-xl border border-white/10 bg-[#0A0A0A] p-6">
          <h3 className="text-lg font-heading font-semibold text-white mb-6">Revenue Trend</h3>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={stats?.chartData || []}>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
              <XAxis 
                dataKey="date" 
                stroke="rgba(255,255,255,0.3)" 
                style={{ fontSize: '12px' }}
                tickFormatter={(value) => value.split('-')[2]}
              />
              <YAxis stroke="rgba(255,255,255,0.3)" style={{ fontSize: '12px' }} />
              <Tooltip 
                contentStyle={{ 
                  backgroundColor: '#0A0A0A', 
                  border: '1px solid rgba(255,255,255,0.1)',
                  borderRadius: '8px',
                  color: '#fff'
                }}
              />
              <Bar dataKey="revenue" fill="#3B82F6" radius={[8, 8, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Recent Activity */}
      <div className="rounded-xl border border-white/10 bg-[#0A0A0A] p-6">
        <h3 className="text-lg font-heading font-semibold text-white mb-4">Recent Activity</h3>
        <div className="space-y-3">
          {[
            { user: 'John Smith', action: 'completed a build', time: '2 minutes ago', type: 'success' },
            { user: 'Emma Johnson', action: 'upgraded to Enterprise', time: '15 minutes ago', type: 'info' },
            { user: 'Michael Chen', action: 'started a new build', time: '23 minutes ago', type: 'info' },
            { user: 'Sarah Williams', action: 'signed up', time: '1 hour ago', type: 'success' },
          ].map((activity, index) => (
            <div key={index} className="flex items-center justify-between py-3 border-b border-white/5 last:border-0">
              <div className="flex items-center gap-3">
                <div className={`w-2 h-2 rounded-full ${activity.type === 'success' ? 'bg-emerald-500' : 'bg-blue-500'}`} />
                <p className="text-sm text-white">
                  <span className="font-medium">{activity.user}</span> {activity.action}
                </p>
              </div>
              <span className="text-xs text-muted-foreground">{activity.time}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
